import { useMemo, useRef, useState, useEffect } from 'react';
import type Phaser from 'phaser';

import { CardCollection } from './components/CardCollection';
import { Lobby } from './components/Lobby';
import { Shop } from './components/Shop';
import {
  CARD_PACK_PRICES,
  addOwnedCardToCollection,
  drawCardFromPack,
  readCardCollectionState,
  toggleEquippedCard,
  writeCardCollectionState,
} from './components/cardCollectionStore';
import { createPhaserGame } from './game/PhaserGame';
import { getMetaProgression, type MetaUpgradeLevels } from './game/systems/MetaProgression';
import { GameDebugPanel } from './components/game/GameDebugPanel';

type Screen = 'lobby' | 'cards' | 'shop' | 'game';

function calculatePlayerLevel(upgrades: MetaUpgradeLevels): number {
  return 1 + upgrades.maxHp + upgrades.moveSpeed + upgrades.startingDamage + upgrades.startingLevel;
}

function PhaserScreen({ onBack }: { onBack: () => void }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const gameRef = useRef<Phaser.Game | null>(null);

  useEffect(() => {
    if (!containerRef.current || gameRef.current) return;
    gameRef.current = createPhaserGame(containerRef.current);
    return () => {
      gameRef.current?.destroy(true);
      gameRef.current = null;
    };
  }, []);

  return (
    <div style={{ width: '100%', height: '100vh', position: 'relative' }}>
      <button
        type="button"
        onClick={onBack}
        style={{
          position: 'absolute',
          top: 10,
          left: 10,
          zIndex: 5,
          borderRadius: 10,
          padding: '8px 10px',
          border: '1px solid #2f3f6b',
          background: '#16213e',
          color: '#fff',
        }}
      >
        ← Lobby
      </button>
      <div
        id="game"
        ref={containerRef}
        style={{
          width: '100%',
          height: 'var(--tg-viewport-height, 100dvh)',
          overflow: 'hidden',
          paddingTop: 'env(safe-area-inset-top)',
          paddingRight: 'env(safe-area-inset-right)',
          paddingBottom: 'env(safe-area-inset-bottom)',
          paddingLeft: 'env(safe-area-inset-left)',
          boxSizing: 'border-box',
        }}
      />
      <GameDebugPanel />
    </div>
  );
}

export default function App() {
  const meta = useMemo(() => getMetaProgression(), []);
  const [screen, setScreen] = useState<Screen>('lobby');
  const [, forceUpdate] = useState(0);
  const [collectionState, setCollectionState] = useState(readCardCollectionState);

  const forceMetaRerender = () => forceUpdate((value) => value + 1);

  const coins = meta.getCoins();
  const upgrades = meta.getUpgradeLevels();
  const level = calculatePlayerLevel(upgrades);

  const buyPack = (packType: keyof typeof CARD_PACK_PRICES) => {
    const price = CARD_PACK_PRICES[packType];
    if (!meta.spendCoins(price)) return null;

    const pulledCard = drawCardFromPack(packType);
    setCollectionState((previousState) => {
      const nextState = addOwnedCardToCollection(previousState, pulledCard.id);
      writeCardCollectionState(nextState);
      return nextState;
    });

    forceMetaRerender();
    return pulledCard;
  };

  const toggleEquip = (cardId: string) => {
    setCollectionState((previousState) => {
      const nextState = toggleEquippedCard(previousState, cardId);
      writeCardCollectionState(nextState);
      return nextState;
    });
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', color: '#fff', overflowX: 'hidden' }}>
      {screen === 'lobby' && (
        <Lobby
          coins={coins}
          level={level}
          equippedCount={collectionState.equippedCardIds.length}
          onPlay={() => {
            localStorage.setItem('henry_start_scene', 'Game');
            setScreen('game');
          }}
          onOpenCards={() => setScreen('cards')}
          onOpenShop={() => setScreen('shop')}
        />
      )}

      {screen === 'cards' && <CardCollection state={collectionState} onBack={() => setScreen('lobby')} onToggleEquip={toggleEquip} />}

      {screen === 'shop' && <Shop coins={coins} onBack={() => setScreen('lobby')} onBuyPack={buyPack} />}

      {screen === 'game' && <PhaserScreen onBack={() => setScreen('lobby')} />}
    </div>
  );
}